import React, { useState, useEffect, useRef } from 'react';
import { Search, Building2, Wrench } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://u5aw5omfkd.execute-api.us-east-2.amazonaws.com/dev';

const emptyVendor = {
  vendor_id: '',
  company_name: '',
  trade: '',
  scopes: [],
  rating: '',
  union: false,
  license_number: '',
  company_notes: '',
  status: 'active',
  contacts: [],
  address: { street: '', city: '', state: '', zip: '' }
};

const normalizeVendor = (vendor) => ({
  ...emptyVendor,
  ...vendor,
  contacts: vendor?.contacts ? vendor.contacts.map(c => ({ name: '', email: '', office_phone: '', cell_phone: '', ...c })) : [],
  scopes: vendor?.scopes || [],
  address: { ...emptyVendor.address, ...(vendor?.address || {}) }
});

const normalizeVendorIdToken = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
);

const buildVendorIdBase = (vendor) => {
  return normalizeVendorIdToken(vendor?.company_name);
};

const makeUniqueVendorId = (baseVendorId, existingVendorIds = new Set()) => {
  if (!baseVendorId) {
    return '';
  }

  if (!existingVendorIds.has(baseVendorId)) {
    return baseVendorId;
  }

  let suffix = 2;
  let candidate = `${baseVendorId}_${suffix}`;
  while (existingVendorIds.has(candidate)) {
    suffix += 1;
    candidate = `${baseVendorId}_${suffix}`;
  }
  return candidate;
};

const VendorManagement = () => {
  const [searchType, setSearchType] = useState('company'); // 'company' or 'scope'
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [visitedVendors, setVisitedVendors] = useState(new Set());
  const [mode, setMode] = useState('view'); // view | edit | new
  const [vendorForm, setVendorForm] = useState(emptyVendor);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const selectedVendorRef = useRef(null);
  const vendorFormRef = useRef(null);

  const searchVendors = async () => {
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    setVisitedVendors(new Set()); // Clear visited history on new search
    resetMessages();
    try {
      const response = await fetch(`${API_BASE}/vendors/search?type=${searchType}&term=${encodeURIComponent(searchTerm)}`);
      if (!response.ok) {
        throw new Error(`Search failed (${response.status})`);
      }
      const data = await response.json();
      setVendors(data.vendors || []);
      setSaveSuccess('');
    } catch (error) {
        console.error('Search failed:', error);
        setSaveError(error.message || 'Search failed');
        setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchVendors();
    }
  };

  const openVendorDetail = (vendor) => {
    setSelectedVendor(vendor);
    setVisitedVendors(prev => new Set([...prev, vendor.vendor_id]));
    setMode('view');
    resetMessages();
  };

  const resetMessages = () => {
    setSaveError('');
    setSaveSuccess('');
  };

  const startEdit = () => {
    if (!selectedVendor) return;
    setVendorForm(normalizeVendor(selectedVendor));
    setMode('edit');
    resetMessages();
  };

  const startNew = () => {
    setSelectedVendor(null);
    setVendorForm(emptyVendor);
    setMode('new');
    resetMessages();
  };

  const cancelForm = () => {
    setMode('view');
    setVendorForm(emptyVendor);
    resetMessages();
  };

  const updateField = (field, value) => {
    setVendorForm((prev) => {
      if (field === 'company_name' && mode === 'new') {
        const nextVendor = {
          ...prev,
          company_name: value
        };
        return {
          ...nextVendor,
          vendor_id: buildVendorIdBase(nextVendor)
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const updateAddressField = (field, value) => {
    setVendorForm((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  };

  const updateScopes = (value) => {
    const scopes = value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    setVendorForm((prev) => ({ ...prev, scopes }));
  };

  const addContact = () => {
    setVendorForm((prev) => ({
      ...prev,
      contacts: [...prev.contacts, { name: '', email: '', office_phone: '', cell_phone: '' }]
    }));
  };

  const updateContactField = (index, field, value) => {
    setVendorForm((prev) => {
      const contacts = [...prev.contacts];
      contacts[index] = { ...contacts[index], [field]: value };
      return { ...prev, contacts };
    });
  };

  const removeContact = (index) => {
    setVendorForm((prev) => {
      const contacts = prev.contacts.filter((_, i) => i !== index);
      return { ...prev, contacts };
    });
  };

  const loadExistingVendorIdSet = async () => {
    const response = await fetch(`${API_BASE}/vendors/search?type=company&term=${encodeURIComponent(' ')}`);
    if (!response.ok) {
      throw new Error(`Unable to load vendors for id check (${response.status})`);
    }

    const data = await response.json();
    return new Set(
      (data.vendors || [])
        .map((vendor) => String(vendor?.vendor_id || '').trim())
        .filter(Boolean)
    );
  };

  const saveVendor = async () => {
    resetMessages();
    if (!vendorForm.company_name || !vendorForm.trade) {
      setSaveError('company_name and trade are required.');
      return;
    }

    setSaving(true);
    try {
      let computedVendorId = String(vendorForm.vendor_id || '').trim();
      if (mode === 'new') {
        const baseVendorId = buildVendorIdBase(vendorForm);
        if (!baseVendorId) {
          setSaveError('Company name is required to generate vendor_id.');
          return;
        }

        try {
          const existingVendorIds = await loadExistingVendorIdSet();
          computedVendorId = makeUniqueVendorId(baseVendorId, existingVendorIds);
        } catch (error) {
          console.warn('Falling back to current vendor list for id collision check:', error);
          const fallbackVendorIds = new Set(
            vendors
              .map((vendor) => String(vendor?.vendor_id || '').trim())
              .filter(Boolean)
          );
          computedVendorId = makeUniqueVendorId(baseVendorId, fallbackVendorIds);
        }
      } else if (!computedVendorId) {
        setSaveError('vendor_id is required for edits.');
        return;
      }

      setVendorForm((prev) => ({ ...prev, vendor_id: computedVendorId }));

      const payload = {
        ...vendorForm,
        vendor_id: computedVendorId,
        scopes: vendorForm.scopes || [],
        contacts: vendorForm.contacts || [],
        address: vendorForm.address || {},
      };
      const url = `${API_BASE}/vendors/${encodeURIComponent(computedVendorId)}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Save failed');
      }

      const updatedVendor = normalizeVendor(payload);
      setVendors((prev) => {
        const idx = prev.findIndex(v => v.vendor_id === updatedVendor.vendor_id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updatedVendor;
          return next;
        }
        return [updatedVendor, ...prev];
      });
      setSelectedVendor(updatedVendor);
      setMode('view');
      setSaveSuccess('Vendor saved');
    } catch (error) {
      console.error('Save failed:', error);
      setSaveError(error.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (selectedVendor && selectedVendorRef.current) {
      selectedVendorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedVendor]);

  useEffect(() => {
    if ((mode === 'edit' || mode === 'new') && vendorFormRef.current) {
      vendorFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [mode]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Vendor Management</h1>
      
      {/* Search Controls */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <button
            onClick={() => setSearchType('company')}
            className={`flex items-center gap-2 px-4 py-2 rounded ${
              searchType === 'company' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            <Building2 size={16} />
            Search by Company
          </button>
          <button
            onClick={() => setSearchType('scope')}
            className={`flex items-center gap-2 px-4 py-2 rounded ${
              searchType === 'scope' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            <Wrench size={16} />
            Search by Scope
          </button>
          <button
            onClick={startNew}
            className="ml-auto px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700"
          >
            New
          </button>
        </div>
        
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={`Search by ${searchType}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={searchVendors}
            disabled={loading || !searchTerm.trim()}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
          >
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {(mode === 'edit' || mode === 'new') && (
        <div className="bg-white rounded-lg shadow p-4 mb-6" ref={vendorFormRef}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
            <h3 className="text-lg font-semibold">
              {mode === 'edit' ? 'Edit Vendor' : 'New Vendor'}
            </h3>
            <div className="text-sm text-gray-500">Required: company_name and trade</div>
          </div>

          {saveError && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{saveError}</div>}
          {saveSuccess && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">{saveSuccess}</div>}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Vendor ID *</label>
              <input
                type="text"
                value={vendorForm.vendor_id}
                onChange={(e) => updateField('vendor_id', e.target.value)}
                readOnly={mode === 'new'}
                className={`mt-1 w-full border rounded px-3 py-2 ${mode === 'new' ? 'bg-gray-50 text-gray-600' : ''}`}
                placeholder="e.g., abc_electric"
              />
              {mode === 'new' && (
                <div className="mt-1 text-xs text-gray-500">
                  Auto-generated from company name. If that id already exists, save appends a numeric suffix.
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name *</label>
              <input
                type="text"
                value={vendorForm.company_name}
                onChange={(e) => updateField('company_name', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Trade *</label>
              <input
                type="text"
                value={vendorForm.trade}
                onChange={(e) => updateField('trade', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
                placeholder="e.g., Electrical"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={vendorForm.status}
                onChange={(e) => updateField('status', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Rating</label>
              <input
                type="text"
                value={vendorForm.rating}
                onChange={(e) => updateField('rating', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
                placeholder="A / B / C"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">License #</label>
              <input
                type="text"
                value={vendorForm.license_number}
                onChange={(e) => updateField('license_number', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Union</label>
              <select
                value={vendorForm.union ? 'true' : 'false'}
                onChange={(e) => updateField('union', e.target.value === 'true')}
                className="mt-1 w-full border rounded px-3 py-2"
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Scopes (comma separated)</label>
              <input
                type="text"
                value={vendorForm.scopes.join(', ')}
                onChange={(e) => updateScopes(e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
                placeholder="Concrete, Electrical, Plumbing"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Address - Street</label>
              <input
                type="text"
                value={vendorForm.address.street}
                onChange={(e) => updateAddressField('street', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Address - City</label>
              <input
                type="text"
                value={vendorForm.address.city}
                onChange={(e) => updateAddressField('city', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Address - State</label>
              <input
                type="text"
                value={vendorForm.address.state}
                onChange={(e) => updateAddressField('state', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
                maxLength={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Address - ZIP</label>
              <input
                type="text"
                value={vendorForm.address.zip}
                onChange={(e) => updateAddressField('zip', e.target.value)}
                className="mt-1 w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Company Notes</label>
            <textarea
              value={vendorForm.company_notes}
              onChange={(e) => updateField('company_notes', e.target.value)}
              className="mt-1 w-full border rounded px-3 py-2"
              rows={3}
            />
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-gray-700">Contacts</div>
              <button
                onClick={addContact}
                className="text-sm text-blue-700 hover:text-blue-900"
                type="button"
              >
                + Add contact
              </button>
            </div>
            {vendorForm.contacts.length === 0 && (
              <div className="text-sm text-gray-500">No contacts yet.</div>
            )}
            <div className="space-y-3">
              {vendorForm.contacts.map((contact, index) => (
                <div key={index} className="border rounded p-3 bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <div className="font-medium text-sm">Contact {index + 1}</div>
                    <button
                      type="button"
                      onClick={() => removeContact(index)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Name</label>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => updateContactField(index, 'name', e.target.value)}
                        className="mt-1 w-full border rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Email</label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContactField(index, 'email', e.target.value)}
                        className="mt-1 w-full border rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Office Phone</label>
                      <input
                        type="text"
                        value={contact.office_phone}
                        onChange={(e) => updateContactField(index, 'office_phone', e.target.value)}
                        className="mt-1 w-full border rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700">Cell Phone</label>
                      <input
                        type="text"
                        value={contact.cell_phone}
                        onChange={(e) => updateContactField(index, 'cell_phone', e.target.value)}
                        className="mt-1 w-full border rounded px-3 py-2"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex gap-3">
            <button
              onClick={saveVendor}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={cancelForm}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {vendors.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b" ref={selectedVendorRef}>
            <h2 className="text-lg font-semibold">Search Results ({vendors.length})</h2>
            {selectedVendor && (
              <div className="mt-3 p-4 border rounded-lg bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="text-sm text-gray-500 uppercase tracking-wide">Selected Vendor</div>
                    <h3 className="text-xl font-bold text-blue-700">{selectedVendor.company_name}</h3>
                    <p className="text-gray-700">{selectedVendor.trade}</p>
                    {selectedVendor.scopes?.length > 0 && (
                      <p className="text-sm text-gray-600 mt-1">{selectedVendor.scopes.join(', ')}</p>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-600 space-y-1">
                    <div><span className="font-medium">Rating:</span> {selectedVendor.rating}</div>
                    <div><span className="font-medium">Union:</span> {selectedVendor.union ? 'Yes' : 'No'}</div>
                    <div><span className="font-medium">License:</span> {selectedVendor.license_number || 'N/A'}</div>
                    <div>
                      {selectedVendor.address?.city}, {selectedVendor.address?.state} {selectedVendor.address?.zip}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap justify-end gap-3 mt-3">
                  <button
                    onClick={startEdit}
                    className="px-3 py-1 text-sm text-blue-700 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => { setSelectedVendor(null); setMode('view'); }}
                    className="px-3 py-1 text-sm text-gray-700 hover:text-gray-900"
                  >
                    Close
                  </button>
                </div>

                {selectedVendor.contacts?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-semibold text-gray-700">Contacts</div>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {selectedVendor.contacts.map((contact, index) => (
                        <div key={index} className="border rounded-md p-3 bg-white shadow-sm min-w-[220px]">
                          <div className="font-medium">{contact.name}</div>
                          <div className="text-sm text-gray-600 space-y-1">
                            {contact.email && <div>{contact.email}</div>}
                            {contact.office_phone && <div>Office: {contact.office_phone}</div>}
                            {contact.cell_phone && <div>Cell: {contact.cell_phone}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedVendor.company_notes && (
                  <div className="mt-3">
                    <div className="text-sm font-semibold text-gray-700">Notes</div>
                    <p className="text-sm text-gray-600 mt-1">{selectedVendor.company_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="divide-y">
            {vendors.map((vendor) => {
              const isVisited = visitedVendors.has(vendor.vendor_id);
              return (
                <div
                  key={vendor.vendor_id}
                  onClick={() => openVendorDetail(vendor)}
                  className="p-4 hover:bg-blue-50 transition-colors"
                  style={{ cursor: 'pointer' }}
                  title="Click for details"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className={`font-semibold text-lg ${
                        isVisited ? 'text-purple-600' : 'text-blue-600'
                      }`}>
                        {vendor.company_name}
                      </h3>
                      <p className="text-gray-600">{vendor.trade}</p>
                      {vendor.scopes && vendor.scopes.length > 0 && (
                        <p className="text-sm text-gray-500 mt-1">
                          {vendor.scopes.join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="text-right text-sm text-gray-500">
                      <div>Rating: {vendor.rating}</div>
                      <div>{vendor.address?.city}, {vendor.address?.state}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
